import React from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { formatSponsorUntil, isSponsorActive } from '../utils/sponsor';
import type { ForumBase } from '../types/forum';

interface Props {
  forum: Pick<ForumBase, 'is_sponsored' | 'sponsored_until'>;
  darkMode: boolean;
  variant?: 'pill' | 'card';
  onTrackClick?: () => void;
  className?: string;
}

const SponsoredBadge: React.FC<Props> = ({
  forum,
  darkMode,
  variant = 'pill',
  onTrackClick,
  className = '',
}) => {
  if (!isSponsorActive(forum)) return null;

  const until = formatSponsorUntil(forum.sponsored_until);
  const styles =
    variant === 'card'
      ? darkMode
        ? 'bg-amber-900/40 border-amber-700/50 text-amber-200'
        : 'bg-amber-50 border-amber-200 text-amber-800'
      : darkMode
        ? 'bg-amber-900/50 text-amber-300'
        : 'bg-amber-100 text-amber-700';

  const inner = (
    <>
      <Star size={variant === 'card' ? 18 : 14} className="fill-current shrink-0" />
      <span className="font-medium">Sponsorlu</span>
      {until && (
        <span className={`text-xs ${darkMode ? 'text-amber-400/90' : 'text-amber-600'}`}>
          · {until}&apos;e kadar
        </span>
      )}
    </>
  );

  if (variant === 'card') {
    return (
      <div
        className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm ${styles} ${className}`}
      >
        {inner}
        <Link
          to="/sponsor"
          className={`ml-auto text-xs underline ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}
          onClick={(e) => e.stopPropagation()}
        >
          Sponsorluk nedir?
        </Link>
      </div>
    );
  }

  return (
    <span
      role={onTrackClick ? 'button' : undefined}
      tabIndex={onTrackClick ? 0 : undefined}
      onClick={onTrackClick}
      onKeyDown={onTrackClick ? (e) => e.key === 'Enter' && onTrackClick() : undefined}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs cursor-default ${styles} ${className}`}
      title={until ? `Sponsorlu içerik — ${until} tarihine kadar` : 'Sponsorlu içerik'}
    >
      {inner}
    </span>
  );
};

export default SponsoredBadge;
